// 2023-03-22 11:37:30 A component for group chat only

import {
  ActionIcon,
  Box,
  Group,
  Input,
  Tooltip,
  Button,
  Stack,
  Text,
  Image,
  Modal,
  Alert,
  Center,
  useMantineTheme,
  LoadingOverlay,
  Loader,
} from "@mantine/core";
import React, { useState, useEffect, useRef } from "react";
import {
  IconMoodHappy,
  IconPhoto,
  IconCirclePlus,
  IconAlertCircle,
} from "@tabler/icons";
import ChatInput from "../components/ChatInput";
import dayjs from "dayjs";
import { io, Socket } from "socket.io-client";
import { URL } from "../services/api";
import ChatHeader from "../components/ChatHeader";
import { LoginFormValues } from "./Login";
import { User } from "../App";

interface Props {
  user: User;
  setUser: (user: User | ((prev: User) => User)) => void;
  setIsLogin: (isLogin: boolean) => void;
}

interface Message {
  id: number;
  username: string;
  text: string;
  time: string;
  type: string;
  blob: Blob;
}

function Chat({ user, setUser, setIsLogin }: Props) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<{
    image: boolean;
    alert: boolean;
  }>({ image: false, alert: false });
  const [imageSrc, setImageSrc] = useState<string>("");
  //const [isManualDisconnection, setIsManualDisconnection] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState<boolean>(false);

  const msgRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);

  const theme = useMantineTheme();

  useEffect(() => {
    let sessionID = window.sessionStorage.getItem("sessionID");
    if (!sessionID) {
      sessionID = Date.now() + Math.random() + "";
      window.sessionStorage.setItem("sessionID", sessionID);
      setUser((prev) => ({ ...prev, id: sessionID as string }));
      window.sessionStorage.setItem(
        "user",
        JSON.stringify({ ...user, id: sessionID })
      );
    }

    //console.log("session id", sessionID);

    const socket =
      socketRef.current ||
      io(URL, {
        reconnection: true,
        auth: {
          sessionID,
          name: user.username,
          room: user.password,
        },
      });
    socketRef.current = socket;

    socket.io.on("reconnect_attempt", (attempt) => {
      //console.log("reconnect processing??..");
      setIsReconnecting(true);
    });

    socket.on("connect", () => {
      //console.log("connect?", socket.connected, socket.id);
      setIsReconnecting(false);
    });

    socket.on("message", (data: Message) => {
      //console.log("message data", data, data.text, "blob is", data.blob);
      setMessages((prevMessages) => [...prevMessages, data]);
      //console.log("msgRef", msgRef.current);
    });

    //socket.emit("user", { name: user.username, room: user.password });

    // Get all the online users
    socket.on("users", (users: User[]) => {
      //console.log("users: ", users);
      setUsers(users);
    });

    // socket.on("userId", (userId) => {
    //   //console.log("user id", userId);
    //   setUser((prev) => ({ ...prev, id: userId }));
    //   window.sessionStorage.setItem(
    //     "user",
    //     JSON.stringify({ ...user, id: userId })
    //   );
    // });

    socket.on("disconnect", () => {
      console.log("socket is still connected? ", socket.connected);
      //setIsModalOpen({ alert: true });
      //setMessages([]);

      //socket.emit("leave", { name: username, room: password });
      // setUsername('')   // Set username to empty after sending leave message
      //  setMessages((prevMessages) => [
      //    ...prevMessages,
      //    {
      //      username: "chatbot",
      //      text: "Your are offline, try to login again  :(",
      //      type: "text",
      //      time: dayjs(),
      //    },
      //  ]);
      //  setUsers(""); // Set online users to empty, using empty sting rather than array to avoid confusion when checking online status on ChatHeader
      //  inputRef.current.dataset.placeholder =
      //    "Your are offline, try to login again  :(";
    });

    return () => {
      socket.removeAllListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Code below to fix the scrollIntoView always scroll to previous message rather than current one position issue
  useEffect(() => {
    msgRef?.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  const handleImageClick = (blob: Blob) => {
    setIsModalOpen((prev) => ({ ...prev, image: true }));
    setImageSrc(window.URL.createObjectURL(blob));
  };

  const handleReconnectClick = () => {
    window.location.reload();
    //   setIsModalOpen({ alert: false });
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        background: "white",
      }}
    >
      <LoadingOverlay
        visible={isReconnecting}
        overlayBlur={1}
        overlayOpacity={0.3}
        loader={
          <Stack align='center'>
            <Loader />
            <Text>重新自动连接中，请稍后。。。</Text>
          </Stack>
        }
      />
      <Box>
        <ChatHeader
          room={user.password}
          users={users}
          socket={socketRef.current}
          setIsReconnectionModalOpen={setIsModalOpen}
          setMessages={setMessages}
          setIsLogin={setIsLogin}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {messages.map((message, index) => (
          <Box
            key={index}
            m='1rem'
            p='0.5rem 1rem'
            sx={(theme) => ({
              width: "max-content",
              maxWidth: "80%",
              borderRadius: "1rem",
              background:
                String(message.id) === String(user.id)
                  ? theme.colors.lime[4]
                  : theme.colors.gray[0], // "yellow.9",
              alignSelf:
                String(message.id) === String(user.id)
                  ? "flex-end"
                  : "flex-start",
              //color: "white",
            })}
          >
            <Text size='xs'>
              {message.username} {dayjs(message.time).format("h:mm a")}
            </Text>
            {message.type === "text" ? (
              <Text>{message.text}</Text>
            ) : message.type === "image" ? (
              <Box
                sx={{ minHeight: "170px" }} // Set as 16:9 ratio with 300 width
              >
                <Image
                  width={300}
                  src={window.URL.createObjectURL(
                    message.blob instanceof Blob
                      ? message.blob
                      : new Blob([message.blob])
                  )}
                  onClick={() =>
                    handleImageClick(
                      message.blob instanceof Blob
                        ? message.blob
                        : new Blob([message.blob])
                    )
                  }
                />
              </Box>
            ) : message.type === "video" ? (
              <video controls width='300'>
                <source
                  src={window.URL.createObjectURL(
                    message.blob instanceof Blob
                      ? message.blob
                      : new Blob([message.blob])
                  )}
                />
              </video>
            ) : null}
          </Box>
        ))}
        <Box ref={msgRef}></Box>
      </Box>
      <Box>
        <ChatInput
          user={user}
          socket={socketRef.current}
          setMessages={setMessages}
        />
      </Box>
      <Modal
        opened={!!Object.values(isModalOpen)[0]}
        withCloseButton={false}
        onClose={() => setIsModalOpen((prev) => ({ ...prev, image: false }))}
        closeOnClickOutside={!!isModalOpen.image}
        closeOnEscape={!!isModalOpen.image}
        //fullScreen
        size='90%'
        padding={0}
        centered
      >
        {isModalOpen.image && <Image src={imageSrc} />}
        {isModalOpen.alert && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title='你已经离线'
            color='orange'
          >
            你已经处于离线状态，所有历史消息都已被清空。你可以尝试点击下面按钮重新进入聊天室开启新的聊天。
            {/* 你由于长时间离开已经处于离线状态，所有历史消息都已被清空。你可以尝试点击下面按钮重新进入聊天室。 */}
            <Center mt='xl' mr='xl'>
              <Button
                variant='gradient'
                gradient={{ from: "teal", to: "lime", deg: 105 }}
                onClick={handleReconnectClick} //window.location.reload()
              >
                重新进入聊天室
              </Button>
            </Center>
          </Alert>
        )}
      </Modal>
    </Box>
  );
}

export default Chat;
